/**
 *      ▄ █
 *    █ █ █▄▄  LOADBALANCER CLIENT
 *    █ █ ▄ █
 *    ▀     ▀  @copyright Copyright (c) 2025
 */
import { AddEditRegionComponent } from './addEditRegion/add-edit-region.component';
import { BaseComponent } from '../../../../../core/components';
import { Component, ComponentRef, EventEmitter, Input, Output, Type, ViewChild, ViewContainerRef } from '@angular/core';
import { ErrorResponseModel } from '../../../../../core/models';
import {
    LanguageService,
    LogService,
    ModalService,
    NavigationService,
    NotifyService,
    RbacService,
    TransactionService
} from '../../../../../core/services';
import { RegionDetailsComponent } from './region-details/region-details.component';
import { RegionsApiService } from '../../http/regions.api.service';
import { Router } from '@angular/router';
import { WizardService } from '../../services';
import { catchError, concatMap, mergeMap, takeUntil } from 'rxjs/operators';
import { from, Observable, of, Subscriber } from 'rxjs';

@Component({
    selector: 'app-lb-regions',
    templateUrl: './regions.component.html',
    styleUrls: ['./regions.component.scss']
})
export class RegionsComponent extends BaseComponent {
    /**
     * Reference to embedded component container.
     */
    @ViewChild('embeddedComponentContainer', {
        read: ViewContainerRef,
        static: true
    }) public embeddedComponentContainer: any;

    /**
     * Component embedded in wizard.
     */
    @Input() public embedded: boolean = false;

    /**
     * Incoming region id for pre-selection in table.
     * @param key
     */
    @Input() set selectedKey(key: string) {
        if (key) {
            this._setSelectedKey = key;
            this.selectedRowObject = [this.tableRows.find(row => row.id === this._setSelectedKey)];
        }
    }

    /**
     * Output selected region.
     */
    @Output() public selectedRegion: EventEmitter<string> = new EventEmitter<string>();

    /**
     * Store the table rows.
     */
    public tableRows: any[] = [];

    /**
     * Selected table rows.
     */
    public selectedRows: any[] = [];

    /**
     * Store permissions in lookup.
     */
    public permissionsLookup: any = {};

    /**
     * Embedded page open.
     */
    public isEmbeddedPageOpen: boolean = false;

    /**
     * Store selected row object for table state checks.
     */
    public selectedRowObject: any = [];

    /**
     * Store multi-select actions.
     */
    public multiSelectActions: any[] = [];

    /**
     * Store region data to view subnets.
     */
    public regionData: any;

    /**
     * Shimmer effect active.
     */
    public shimmer: boolean = false;

    /**
     * Progress message that shows when deleting multiples.
     */
    public progressMessage: string;

    /**
     * Store component reference.
     * @private
     */
    private componentReference: ComponentRef<any>;

    /**
     * Store selected row key.
     * @private
     */
    private _setSelectedKey: string;

    constructor(
        private languageService: LanguageService,
        private logService: LogService,
        private modalService: ModalService,
        private navigationService: NavigationService,
        private notifyService: NotifyService,
        private rbacService: RbacService,
        private regionsApiService: RegionsApiService,
        private router: Router,
        private transactionService: TransactionService,
        private wizardService: WizardService
    ) {
        super();
        this.setUpPageData();
        this.registerButtons();
        this.setSelectAll();
    }

    /**
     * Get the selected key.
     */
    get selectedKey() {
        return this._setSelectedKey;
    }

    /**
     * Add region.
     */
    public addRegion(): void {
        if (this.embedded) {
            this.setUpEmbeddedComponent('addRegion');
        } else {
            const routeData = {
                pageType: 'addRegion'
            };
            this.router.navigateByUrl(this.navigationService.getEnduranceUrl('/network/regions/add'),
                { state: routeData }).then().catch();
        }
    }

    /**
     * Link to the region details page.
     * @param data
     */
    public goToRegion(data: any): void {
        if (this.embedded) {
            this.setUpEmbeddedComponent('regionDetails', data);
        } else {
            this.router.navigateByUrl(
                this.navigationService.getEnduranceUrl('/network/regions/' + data.id),
                { state: data }).then().catch();
        }
    }

    /**
     * Edit region.
     * @param data
     */
    public editRegion(data: any): void {
        if (this.embedded) {
            this.setUpEmbeddedComponent('editRegion', data);
        } else {
            const routeData = {
                pageType: 'editRegion'
            };

            this.router.navigateByUrl(
                this.navigationService.getEnduranceUrl('/network/regions/edit/' + data.id),
                { state: routeData }).then().catch();
        }
    }

    /**
     * Form function triggered when a table row is selected.
     * @param event
     */
    public onSelect(event: any[]): void {
        this.selectedRowObject = event;

        if (!this.embedded) {
            this.multiSelectActions[0].buttonAction = this.confirmDeleteMultipleRegions.bind(this, this.selectedRowObject);
        } else {
            this.selectedRegion.emit(event[0].id);
        }
    }

    /**
     * View subnets.
     * @param id
     */
    public viewSubnets(id: string): void {
        this.regionsApiService.getRegion(id)
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (response: any): void => {
                    this.regionData = response;
                    this.modalService.openModalInParent('subnet-list');
                },
                error: (error: ErrorResponseModel): void => {
                    this.logService.error('RegionsComponent: viewSubnets() - failed to fetch subnets', error.message);
                }
            });
    }

    /**
     * Notify user when code has been copied successfully.
     */
    public copySubnet(): void {
        this.notifyService.success('Copied');

        this.shimmer = true;
        const timerId = setTimeout(() => {
            this.shimmer = false;
            clearTimeout(timerId);
        }, 750);
    }

    /**
     * Confirm delete of region
     * @param data
     */
    public confirmDeleteRegion(data: any): void {
        const confirmationObject = {
            title: 'region_delete',
            message: 'Are you sure you would like to delete region ' + data.name + '?',
            callBack: this.deleteRegion.bind(this, data.id),
            options: { translateText: false }
        };

        this.modalService.confirmDecision(confirmationObject);
    }

    /**
     * Confirm deletion of multiple regions.
     * @param selectedRows
     */
    public confirmDeleteMultipleRegions(selectedRows: any[]): void {
        const message: string = `Are you sure you want to delete ${selectedRows.length} regions?`;
        const confirmationObject = {
            title: 'region_delete_multiple_regions',
            message: message,
            callBack: this.deleteMultipleRegionsTransaction.bind(this, selectedRows),
            options: { translateText: false }
        };
        this.modalService.confirmDecision(confirmationObject);
    }

    /**
     * Delete region.
     * @param id
     * @private
     */
    private deleteRegion(id: string): Observable<any> {
        return new Observable((observer: any): void => {
            this.regionsApiService.deleteRegion(id)
                .pipe(takeUntil(this.destroyed$))
                .subscribe({
                    next: (): void => {
                        this.setUpPageData();
                        observer.next();
                        observer.complete();
                    },
                    error: (error: ErrorResponseModel): void => {
                        this.logService.error('RegionsComponent: deleteRegion',
                            'Failed to delete region', error.message);
                        observer.error(error);
                        observer.complete();
                    }
                });
        });
    }

    /**
     * Set select all actions.
     * @private
     */
    private setSelectAll(): void {
        this.multiSelectActions = [
            {
                buttonText: 'region_delete_multiple_regions',
                buttonAction: this.confirmDeleteMultipleRegions.bind(this),
                buttonDisabled: !this.permissionsLookup['delete-region']
            }
        ];
    }

    /**
     * Handle deleting of multiple regions in transaction mode.
     * @param selectedRows
     * @private
     */
    private deleteMultipleRegionsTransaction(selectedRows: any[]): Observable<any> {
        return new Observable((observer: any): void => {
            this.notifyService.muteLocalNotifications();
            this.transactionService.startTransaction().pipe(
                concatMap(() => this.deleteMultipleRegions(selectedRows)),
                concatMap(() => this.commitTransaction()),
                takeUntil(this.destroyed$)
            ).subscribe({
                next: (): void => {
                    this.notifyService.unmuteLocalNotifications();
                    this.notifyService.success('All selected regions have been deleted.');
                    this.progressMessage = '';
                    this.modalService.close('regions-delete-progress');
                },
                error: (error: ErrorResponseModel): void => {
                    observer.error(error);
                    observer.complete();
                },
                complete: (): void => {
                    observer.next();
                    observer.complete();
                }
            });
        });
    }

    /**
     * Delete multiple regions
     * @param selectedRows
     * @private
     */
    private deleteMultipleRegions(selectedRows: any[]): Observable<any> {
        return new Observable((observer) => {
            const translation: string = this.languageService.translate('region_deleting_regions');
            const ofText: string = this.languageService.translate('of');
            let index: number = 1;
            if (selectedRows.length > 1) {
                this.modalService.openModalInParent('regions-delete-progress');
                this.progressMessage = `${translation} ${index} ${ofText} ${selectedRows.length}`;
            }
            const dataToSubmit = from(selectedRows || []);
            const call = dataToSubmit.pipe(
                mergeMap(row => this.regionsApiService.deleteRegion(row.id).pipe(catchError(error => of(error))))
            );
            call.subscribe({
                next: (): void => {
                    if (selectedRows.length > 1) {
                        this.progressMessage = `${translation} ${index} ${ofText} ${selectedRows.length}`;
                    }
                    index++;
                },
                error: (error: ErrorResponseModel): void => {
                    this.logService.error('RegionsComponent:deleteMultipleRegions() error', error.message);
                    observer.error(error);
                },
                complete: (): void => {
                    this.setUpPageData();
                    observer.next();
                    observer.complete();
                }
            });
        });
    }

    /**
     * Commit transaction.
     * @private
     */
    private commitTransaction(): Observable<any> {
        return new Observable((observer: Subscriber<any>): void => {
            this.transactionService.commitTransaction()
                .subscribe({
                    next: (): void => {
                        observer.next();
                        observer.complete();
                    },
                    error: (error: ErrorResponseModel): void => {
                        observer.error(error);
                        observer.complete();
                    }
                });
        });
    }

    /**
     * Set up the embedded component.
     * @param componentId
     * @param data
     * @private
     */
    private setUpEmbeddedComponent(componentId: string, data?: any): void {
        const component: Type<any> = this.getComponent(componentId);
        const embeddedComponent: ComponentRef<any> = this.embeddedComponentContainer.createComponent(component);
        embeddedComponent.instance.embedded = true;
        embeddedComponent.instance.regionId = data?.id;
        embeddedComponent.instance.regionName = data?.name;
        embeddedComponent.instance.tableRows = data?.subnets;
        embeddedComponent.instance.callBack = this.cancelView.bind(this);
        if (componentId === 'addRegion' || componentId === 'editRegion') {
            embeddedComponent.instance.pageType = componentId;
        }
        this.componentReference = embeddedComponent;
        this.wizardService.disableNavigation = true;
        this.isEmbeddedPageOpen = true;
    }

    /**
     * Get component to embed.
     * @param componentId
     * @private
     */
    private getComponent(componentId: string): Type<any> {
        switch (componentId) {
            case 'regionDetails':
                return RegionDetailsComponent;
            case 'addRegion':
            case 'editRegion':
                return AddEditRegionComponent;
        }
    }

    /**
     * Cancel embedded view.
     * @private
     */
    private cancelView(): void {
        this.isEmbeddedPageOpen = false;
        this.wizardService.disableNavigation = false;
        this.setUpPageData();
        this.componentReference.destroy();
    }

    /**
     * Set up page data.
     * @private
     */
    private setUpPageData(): void {
        this.regionsApiService.getRegions()
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (data: any[]): void => {
                    this.tableRows = data;
                },
                error: (error: ErrorResponseModel): void => {
                    this.logService.error('RegionsComponent: setUpPageData',
                        'Failed to get regions data', error.message);
                }
            });
    }

    /**
     * Register buttons.
     * @private
     */
    private registerButtons(): void {
        const buttonReference: any = {
            'region-link': [{ path: '/regions/:id', methods: ['GET'] }],
            'add-region': [{ path: '/regions', methods: ['POST'] }],
            'edit-region': [{ path: '/regions/:id', methods: ['PUT'] }],
            'delete-region': [{ path: '/regions/:id', methods: ['DELETE'] }],
        };

        this.permissionsLookup = this.rbacService.getPermissionsLookup(buttonReference);
    }
}
